import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { canFeature } from '@/lib/perms';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Copy, Trash2, Star, Edit2, ArrowLeft, ArrowUp, ArrowDown, Save } from 'lucide-react';
import { useConfirm, usePrompt } from '@/components/ConfirmDialog';

const CATS = ['Residential', 'Commercial', 'Retail', 'Custom'];
const TYPES = ['text', 'number', 'textarea', 'date', 'dropdown', 'multiselect', 'toggle'];
const uid = () => (crypto.randomUUID?.() || Math.random().toString(36).slice(2));

export default function BriefTemplatesManager() {
  const { user } = useAuth();
  const [confirm, confirmDialog] = useConfirm();
  const [prompt, promptDialog] = usePrompt();
  const [cat, setCat] = useState('Residential');
  const [templates, setTemplates] = useState([]);
  const [editing, setEditing] = useState(null); // full template being edited

  const load = useCallback(async (c) => {
    try { const r = await api.get(`/brief-templates?category=${c}`); setTemplates(r.data); }
    catch { toast.error('Failed to load templates'); }
  }, []);
  useEffect(() => { load(cat); }, [cat, load]);

  if (!canFeature(user, 'manageData'))
    return <div className="glass-card rounded-2xl p-8 text-center text-sm text-[#6B7280]">Brief templates are managed by the Owner or Team members with database access.</div>;

  const create = async () => {
    const name = await prompt({ title: 'New template', label: 'Name', placeholder: 'e.g. Residential — Full Brief', confirmLabel: 'Create' });
    if (!name) return;
    await api.post('/brief-templates', { category: cat, name, blank: true });
    toast.success('Template created'); load(cat);
  };
  const duplicate = async (id) => { await api.post(`/brief-templates/${id}/duplicate`); toast.success('Duplicated'); load(cat); };
  const setDefault = async (id) => { await api.put(`/brief-templates/${id}/default`); toast.success('Set as default'); load(cat); };
  const remove = async (t) => {
    if (t.isDefault) { toast.error('Set another template as default first'); return; }
    if (!(await confirm({ title: 'Delete template?', message: `"${t.name}" will be permanently deleted. This cannot be undone.`, confirmLabel: 'Delete' }))) return;
    try { await api.delete(`/brief-templates/${t.id}`); toast.success('Deleted'); load(cat); }
    catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };
  const openEdit = async (t) => {
    const full = templates.find(x => x.id === t.id);
    setEditing(JSON.parse(JSON.stringify(full)));
  };

  if (editing) return <TemplateEditor template={editing} onBack={() => { setEditing(null); load(cat); }} />;

  return (
    <div className="space-y-4" data-testid="brief-templates-manager">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex gap-1 bg-[#E8E6E1]/50 rounded-xl p-1">
          {CATS.map(c => (
            <button key={c} onClick={() => setCat(c)} data-testid={`brief-cat-${c}`}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${cat === c ? 'bg-white shadow-sm text-[#2C2C2E]' : 'text-[#6B7280]'}`}>{c}</button>
          ))}
        </div>
        <Button onClick={create} className="h-9 bg-[#3A8C8C] hover:bg-[#2E7A7A] text-white rounded-lg" data-testid="brief-template-add">
          <Plus size={15} strokeWidth={1.5} className="mr-1.5" /> Add Template
        </Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {templates.map(t => (
          <div key={t.id} className="glass-card rounded-xl p-4" data-testid={`brief-template-${t.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="text-sm font-semibold text-[#2C2C2E] flex items-center gap-1.5">
                  {t.name} {t.isDefault && <Badge className="bg-[#3A8C8C]/10 text-[#3A8C8C] text-[9px]"><Star size={8} className="mr-0.5" />Default</Badge>}
                </p>
                <p className="text-[11px] text-[#6B7280] mt-0.5">{t.sectionCount} sections · {t.fieldCount} fields · updated {(t.updatedAt || '').slice(0, 10)}</p>
              </div>
            </div>
            <div className="flex items-center gap-1 mt-3">
              <Button size="sm" variant="outline" className="h-7 text-xs rounded-lg border-[#E8E6E1]" onClick={() => openEdit(t)} data-testid={`brief-edit-${t.id}`}><Edit2 size={12} className="mr-1" />Edit</Button>
              <Button size="sm" variant="ghost" className="h-7 text-xs text-[#6B7280]" onClick={() => duplicate(t.id)} data-testid={`brief-dup-${t.id}`}><Copy size={12} /></Button>
              {!t.isDefault && <Button size="sm" variant="ghost" className="h-7 text-xs text-[#3A8C8C]" onClick={() => setDefault(t.id)} data-testid={`brief-default-${t.id}`}><Star size={12} /></Button>}
              {!t.isDefault && <Button size="sm" variant="ghost" className="h-7 text-xs text-[#D94F4F]" onClick={() => remove(t)} data-testid={`brief-del-${t.id}`}><Trash2 size={12} /></Button>}
            </div>
          </div>
        ))}
        {templates.length === 0 && <p className="text-sm text-[#6B7280] col-span-2 text-center py-6">No templates in {cat}. Add one.</p>}
      </div>

      {confirmDialog}
      {promptDialog}
    </div>
  );
}

function TemplateEditor({ template, onBack }) {
  const [sections, setSections] = useState(template.sections || []);
  const [name, setName] = useState(template.name);
  const [fieldModal, setFieldModal] = useState(null); // { sIdx, fIdx?, field }
  const [saving, setSaving] = useState(false);

  const move = (arr, i, dir) => { const j = i + dir; if (j < 0 || j >= arr.length) return arr; const c = [...arr]; [c[i], c[j]] = [c[j], c[i]]; return c; };

  const addSection = () => setSections(s => [...s, { id: uid(), title: 'New Section', order: s.length, fields: [] }]);
  const renameSection = (i, title) => setSections(s => s.map((x, idx) => idx === i ? { ...x, title } : x));
  const delSection = (i) => setSections(s => s.filter((_, idx) => idx !== i));
  const delField = (si, fi) => setSections(s => s.map((sec, idx) => idx === si ? { ...sec, fields: sec.fields.filter((_, j) => j !== fi) } : sec));

  const saveField = (data) => {
    const { sIdx, fIdx } = fieldModal;
    const f = { id: data.id || uid(), label: data.label, type: data.type, options: data.options, placeholder: data.placeholder, required: data.required, order: 0 };
    setSections(s => s.map((sec, idx) => {
      if (idx !== sIdx) return sec;
      const fields = [...sec.fields];
      if (fIdx == null) fields.push(f); else fields[fIdx] = f;
      return { ...sec, fields };
    }));
    setFieldModal(null);
  };

  const persist = async () => {
    setSaving(true);
    try {
      await api.put(`/brief-templates/${template.id}`, { name });
      await api.put(`/brief-templates/${template.id}/sections`, { sections });
      toast.success('Template saved');
      onBack();
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="brief-template-editor">
      <div className="flex items-center justify-between gap-3">
        <button onClick={onBack} className="flex items-center gap-1.5 text-sm text-[#6B7280] hover:text-[#2C2C2E]"><ArrowLeft size={15} /> Back</button>
        <div className="flex items-center gap-2">
          <Input value={name} onChange={e => setName(e.target.value)} className="h-9 w-56 rounded-lg border-[#D4D0C8] text-sm" data-testid="brief-editor-name" />
          <Button onClick={persist} disabled={saving} className="h-9 bg-[#3A8C8C] hover:bg-[#2E7A7A] text-white rounded-lg" data-testid="brief-editor-save"><Save size={14} className="mr-1.5" />{saving ? 'Saving...' : 'Save'}</Button>
        </div>
      </div>

      <div className="space-y-3">
        {sections.map((sec, si) => (
          <div key={sec.id} className="glass-card rounded-xl p-4" data-testid={`editor-section-${si}`}>
            <div className="flex items-center gap-2 mb-2">
              <Input value={sec.title} onChange={e => renameSection(si, e.target.value)} className="h-8 rounded-lg border-[#D4D0C8] text-sm font-medium flex-1" data-testid={`editor-section-title-${si}`} />
              <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-[#6B7280]" onClick={() => setSections(s => move(s, si, -1))}><ArrowUp size={13} /></Button>
              <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-[#6B7280]" onClick={() => setSections(s => move(s, si, 1))}><ArrowDown size={13} /></Button>
              <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-[#D94F4F]" onClick={() => delSection(si)}><Trash2 size={13} /></Button>
            </div>
            <div className="space-y-1.5">
              {sec.fields.map((f, fi) => (
                <div key={f.id} className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#FAFAF8] border border-[#E8E6E1]" data-testid={`editor-field-${si}-${fi}`}>
                  <span className="text-sm text-[#2C2C2E] flex-1">{f.label}</span>
                  <Badge className="bg-[#E8E6E1] text-[#6B7280] text-[9px]">{f.type}</Badge>
                  {f.required && <span className="text-[#D94F4F] text-xs">*</span>}
                  <button onClick={() => setSections(s => s.map((x, idx) => idx === si ? { ...x, fields: move(x.fields, fi, -1) } : x))} className="text-[#6B7280]"><ArrowUp size={12} /></button>
                  <button onClick={() => setSections(s => s.map((x, idx) => idx === si ? { ...x, fields: move(x.fields, fi, 1) } : x))} className="text-[#6B7280]"><ArrowDown size={12} /></button>
                  <button onClick={() => setFieldModal({ sIdx: si, fIdx: fi, field: f })} className="text-[#3A8C8C]"><Edit2 size={12} /></button>
                  <button onClick={() => delField(si, fi)} className="text-[#D94F4F]"><Trash2 size={12} /></button>
                </div>
              ))}
            </div>
            <button onClick={() => setFieldModal({ sIdx: si, fIdx: null, field: null })} className="mt-2 text-xs text-[#3A8C8C] hover:underline flex items-center gap-1" data-testid={`editor-add-field-${si}`}><Plus size={12} /> Add field</button>
          </div>
        ))}
        <button onClick={addSection} className="w-full py-2.5 rounded-xl border border-dashed border-[#D4D0C8] text-sm text-[#6B7280] hover:border-[#3A8C8C] hover:text-[#3A8C8C] transition-colors flex items-center justify-center gap-1.5" data-testid="editor-add-section"><Plus size={14} /> Add section</button>
      </div>

      {fieldModal && <FieldModal field={fieldModal.field} onClose={() => setFieldModal(null)} onSave={saveField} />}
    </div>
  );
}

function FieldModal({ field, onClose, onSave }) {
  const [label, setLabel] = useState(field?.label || '');
  const [type, setType] = useState(field?.type || 'text');
  const [optionsText, setOptionsText] = useState((field?.options || []).join('\n'));
  const [placeholder, setPlaceholder] = useState(field?.placeholder || '');
  const [required, setRequired] = useState(field?.required || false);
  const hasOptions = ['dropdown', 'multiselect'].includes(type);

  const submit = () => {
    if (!label.trim()) { toast.error('Label required'); return; }
    onSave({ id: field?.id, label: label.trim(), type,
      options: hasOptions ? optionsText.split('\n').map(o => o.trim()).filter(Boolean) : [],
      placeholder, required });
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="rounded-2xl" data-testid="brief-field-modal">
        <DialogHeader><DialogTitle className="font-['Manrope']">{field ? 'Edit Field' : 'Add Field'}</DialogTitle></DialogHeader>
        <div className="space-y-3 py-1">
          <div><Label className="text-xs text-[#6B7280]">Label</Label><Input value={label} onChange={e => setLabel(e.target.value)} className="mt-1 rounded-lg border-[#D4D0C8]" data-testid="field-label-input" /></div>
          <div><Label className="text-xs text-[#6B7280]">Type</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger className="mt-1 rounded-lg border-[#D4D0C8]" data-testid="field-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>{TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {hasOptions && (
            <div><Label className="text-xs text-[#6B7280]">Options (one per line)</Label>
              <textarea value={optionsText} onChange={e => setOptionsText(e.target.value)} className="mt-1 w-full h-24 px-3 py-2 text-sm border border-[#D4D0C8] rounded-lg resize-none" data-testid="field-options-input" /></div>
          )}
          {!['toggle'].includes(type) && (
            <div><Label className="text-xs text-[#6B7280]">Placeholder</Label><Input value={placeholder} onChange={e => setPlaceholder(e.target.value)} className="mt-1 rounded-lg border-[#D4D0C8]" /></div>
          )}
          <label className="flex items-center gap-2 text-sm text-[#2C2C2E] cursor-pointer">
            <input type="checkbox" checked={required} onChange={e => setRequired(e.target.checked)} data-testid="field-required-toggle" /> Required field
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-lg border-[#E8E6E1]" onClick={onClose}>Cancel</Button>
          <Button className="rounded-lg bg-[#3A8C8C] hover:bg-[#2E7A7A] text-white" onClick={submit} data-testid="field-save-btn">Save Field</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
